import React, { useEffect, useState, useRef } from 'react';
import { Search, Plus, Edit2, Trash2, X, AlertCircle, Tags, FileDown, Upload, Download } from 'lucide-react';
import { getInventoriSpareparts, getInventoriSparepart, createInventoriSparepart, updateInventoriSparepart, deleteInventoriSparepart, getGudang, getKategoriSparepart, createKategori, downloadInventoriSparepartTemplate, deleteAllInventoriSpareparts, importInventoriSpareparts } from '../../api';
import { useAuth } from '../../AuthContext';
import * as XLSX from 'xlsx';
import ExportExcelModal from '../../components/ExportExcelModal';

const emptyForm = { code: '', name: '', unit: '', price: '', harga_beli: '', harga_jual: '', warranty: '', stock: '', gudang_id: '', min_stok: '', kategori: '', rak: '', stok_per_gudang: {} };

// Total stok per gudang yang terisi di form
const sumPerGudang = (stokMap) => Object.values(stokMap || {}).reduce((a, b) => a + (Number(b) || 0), 0);

export default function Spareparts() {
  const { user, hasMenuAction } = useAuth();
  const fileInputRef = useRef(null);
  const [parts, setParts] = useState([]);
  const [gudangList, setGudangList] = useState([]);
  const [kategoriList, setKategoriList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterKategori, setFilterKategori] = useState('');
  const [filterGudang, setFilterGudang] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [totalAll, setTotalAll] = useState(0);
  const exportAllRef = useRef([]);

  const load = (kategoriFilter) => {
    setLoading(true);
    const params = { search };
    if (kategoriFilter || filterKategori) params.kategori = kategoriFilter || filterKategori;
    if (filterGudang) params.gudang_id = filterGudang;
    Promise.all([getInventoriSpareparts(params), getGudang()])
      .then(([res, gRes]) => { setParts(res.data); setGudangList(gRes.data || []); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { loadKategori(); load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterKategori]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterGudang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh data sparepart + kategori & gudang when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        load();
        loadKategori();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = parts.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase())
  );

  // Deteksi item duplikat (nama yang sama muncul lebih dari 1x)
  const nameCount = {};
  parts.forEach(p => { nameCount[p.name] = (nameCount[p.name] || 0) + 1; });
  const isDuplicate = (p) => (nameCount[p.name] || 0) > 1;
  const dupGroupOf = (p) => isDuplicate(p) ? parts.filter(x => x.name === p.name) : [];

  const formatPrice = (v) => {
    const num = Number(v);
    if (isNaN(num)) return 'Rp 0';
    return `Rp ${num.toLocaleString('id-ID')}`;
  };

  // ---------- Export Excel ----------
  // Teks gudang: rincian per gudang bila ada, kalau tidak nama gudang asalnya
  const gudangTextOf = (p) => {
    const map = p.stok_per_gudang;
    if (map && Object.keys(map).length > 0) {
      return Object.entries(map).map(([gid, stok]) => {
        const g = gudangList.find(x => String(x.id) === String(gid));
        return `${g ? g.nama : 'Gudang #' + gid}: ${stok}`;
      }).join('; ');
    }
    return p.gudang_nama || '';
  };

  const buildExportRows = (list) => list.map((p, i) => ({
    'No': i + 1,
    'Kode': p.code || '',
    'Nama Sparepart': p.name || '',
    'Satuan': p.unit || '',
    'Harga Beli': Number(p.harga_beli) || 0,
    'Harga Jual': Number(p.harga_jual || p.price) || 0,
    'Harga (lama)': Number(p.price) || 0,
    'Stok Total': Number(p.stock) || 0,
    'Min. Stok': Number(p.min_stok) || 0,
    'Stok per Gudang': gudangTextOf(p),
    'Kategori': p.kategori || '',
    'Rak': p.rak || '',
    'Garansi': p.warranty || '',
    'Tanggal Dibuat': p.created_at
      ? new Date(p.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '',
  }));

  const tulisExcelSparepart = (list, suffix) => {
    try {
      if (!list || list.length === 0) {
        alert('Tidak ada data sparepart untuk di-export');
        return;
      }
      const rows = buildExportRows(list);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 5 },   // No
        { wch: 16 },  // Kode
        { wch: 34 },  // Nama Sparepart
        { wch: 10 },  // Satuan
        { wch: 14 },  // Harga Beli
        { wch: 14 },  // Harga Jual
        { wch: 14 },  // Harga (lama)
        { wch: 10 },  // Stok Total
        { wch: 10 },  // Min. Stok
        { wch: 28 },  // Stok per Gudang
        { wch: 16 },  // Kategori
        { wch: 10 },  // Rak
        { wch: 14 },  // Garansi
        { wch: 16 },  // Tanggal Dibuat
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Data Inventori Sparepart');
      const filename = `Data_Inventori_Sparepart${suffix ? '_' + suffix : ''}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Export Excel gagal', err);
      alert('Gagal mengexport Excel. Silahkan coba lagi.');
    }
  };

  // Ambil seluruh data TANPA filter dari server (dipakai opsi "Semua Data")
  const ambilSemuaSparepart = async () => {
    const res = await getInventoriSpareparts({});
    const list = res.data || [];
    exportAllRef.current = list;
    setTotalAll(list.length);
    return list;
  };

  const openExportModal = () => {
    setShowExportModal(true);
    // Hitung jumlah seluruh data (halaman ini bisa sedang difilter kategori/gudang oleh server)
    ambilSemuaSparepart().catch(() => { setTotalAll(parts.length); });
  };

  const exportAllData = async () => {
    setShowExportModal(false);
    let list = exportAllRef.current;
    if (!list || list.length === 0) {
      setExporting(true);
      try {
        list = await ambilSemuaSparepart();
      } catch (err) {
        console.error('Export semua data gagal', err);
        alert('Gagal mengambil seluruh data sparepart. Silahkan coba lagi.');
        setExporting(false);
        return;
      }
      setExporting(false);
    }
    tulisExcelSparepart(list, '');
  };

  const exportFilterSummary = [
    search ? `Pencarian: "${search}"` : '',
    filterKategori ? `Kategori: ${filterKategori}` : '',
    filterGudang ? `Gudang: ${(gudangList.find(g => String(g.id) === String(filterGudang)) || {}).nama || filterGudang}` : '',
  ].filter(Boolean);

  const openCreate = () => { setEditingPart(null); setForm(emptyForm); setError(''); loadKategori(); setShowModal(true); };
  const loadKategori = async () => {
    try {
      const res = await getKategoriSparepart();
      setKategoriList(res.data?.kategori || []);
    } catch (err) {
      setKategoriList([]);
    }
  };

  const openEdit = async (part) => {
    setEditingPart(part); setError('');
    try {
      await loadKategori();
      const res = await getInventoriSparepart(part.id);
      const p = res.data;
      setForm({
        code: p.code || '', name: p.name || '', unit: p.unit || '',
        price: p.price ? String(p.price) : '', harga_beli: p.harga_beli ? String(p.harga_beli) : '',
        harga_jual: p.harga_jual ? String(p.harga_jual) : '', warranty: p.warranty || '',
        stock: p.stock !== undefined ? String(p.stock) : '0', gudang_id: p.gudang_id || '',
        min_stok: p.min_stok || '0', kategori: p.kategori || '', rak: p.rak || '',
        stok_per_gudang: p.stok_per_gudang || {},
      });
      setShowModal(true);
    } catch (err) { setError('Gagal memuat data'); }
  };

  const handleSave = async () => {
    setError(''); if (!form.name) { setError('Nama sparepart harus diisi'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code, name: form.name, unit: form.unit,
        price: parseFloat(form.price) || 0, harga_beli: parseFloat(form.harga_beli) || 0,
        harga_jual: parseFloat(form.harga_jual) || 0, warranty: form.warranty,
        stock: parseInt(form.stock) || 0, gudang_id: parseInt(form.gudang_id) || 0,
        min_stok: parseInt(form.min_stok) || 0, kategori: form.kategori, rak: form.rak,
        stok_per_gudang: Object.entries(form.stok_per_gudang || {})
          .map(([gid, stok]) => ({ gudang_id: Number(gid), stok: Number(stok) || 0 }))
          .filter(sg => sg.gudang_id > 0),
      };
      if (editingPart) await updateInventoriSparepart(editingPart.id, payload);
      else await createInventoriSparepart(payload);
      setShowModal(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (part) => {
    if (!window.confirm(`Yakin hapus "${part.name}"?`)) return;
    try { await deleteInventoriSparepart(part.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadInventoriSparepartTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_import_inventori_sparepart.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Gagal download template');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importInventoriSpareparts(file);
      alert(res.data?.message || 'Import berhasil');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal import file');
    }
    setImporting(false);
    e.target.value = '';
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ PERINGATAN! Anda akan menghapus SEMUA data sparepart!\n\nTindakan ini TIDAK BISA DIBATALKAN.\n\nLanjutkan?')) return;
    if (!window.confirm('⚠️ KONFIRMASI AKHIR!\n\nApakah Anda YAKIN ingin menghapus semua sparepart?\n\nData yang sudah dihapus tidak bisa dikembalikan!')) return;
    setDeletingAll(true);
    try {
      await deleteAllInventoriSpareparts();
      alert('Semua data sparepart berhasil dihapus');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus data');
    }
    setDeletingAll(false);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Katalog Sparepart</h2>
            <p className="page-desc">
              {parts.length} item sparepart
              {Object.values(nameCount).filter(c => c > 1).length > 0 && (
                <span style={{ color: '#b45309' }}> · {Object.values(nameCount).filter(c => c > 1).length} item duplikat ⚠</span>
              )}
            </p>
          </div>
          <div className="page-header-actions">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
            <button className="btn btn-secondary" onClick={handleDownloadTemplate}><FileDown size={16} /><span>Download Template</span></button>
            <button className="btn btn-secondary" onClick={handleImportClick} disabled={importing}><Upload size={16} /><span>{importing ? 'Mengimpor...' : 'Import Excel'}</span></button>
            <button className="btn btn-secondary" onClick={openExportModal} disabled={exporting}><Download size={16} /><span>{exporting ? 'Menyiapkan...' : 'Export Excel'}</span></button>
            {hasMenuAction('inventori', 'create') && (
              <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /><span>Tambah Sparepart</span></button>
            )}
            {user?.role === 'admin' && (
              <button className="btn btn-danger" onClick={handleDeleteAll} disabled={deletingAll}><Trash2 size={16} /><span>{deletingAll ? 'Menghapus...' : 'Hapus Semua Data'}</span></button>
            )}
          </div>
        </div>
      </div>
      <div className="filters">
        <div className="search-box" style={{ flex: 1, maxWidth: 400 }}>
          <Search size={18} />
          <input type="text" placeholder="Cari sparepart..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterKategori} onChange={e => setFilterKategori(e.target.value)} style={{ minWidth: 160 }}>
          <option value="">Semua Kategori</option>
          {kategoriList.map((k, i) => (
            <option key={i} value={k.kategori}>{k.kategori} ({k.jumlah_item})</option>
          ))}
        </select>
        <select value={filterGudang} onChange={e => setFilterGudang(e.target.value)} style={{ minWidth: 150 }}>
          <option value="">Semua Gudang</option>
          {gudangList.map(g => (
            <option key={g.id} value={g.id}>{g.nama}</option>
          ))}
        </select>
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'center' }}>No</th>
                <th>Kode</th>
                <th>Nama Sparepart</th>
                <th>Satuan</th>
                <th style={{ textAlign: 'right' }}>Harga Beli</th>
                <th style={{ textAlign: 'right' }}>Harga Jual</th>
                <th style={{ textAlign: 'center' }}>Stok</th>
                <th style={{ textAlign: 'center' }}>Min</th>
                <th>Gudang</th>
                <th>Kategori</th>
                <th>Rak</th>
                <th>Garansi</th>
                {(hasMenuAction('inventori', 'edit') || hasMenuAction('inventori', 'delete')) && (
                  <th style={{ width: 64, textAlign: 'center' }}>Aksi</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((part, idx) => {
                const dup = isDuplicate(part);
                const dupGroup = dupGroupOf(part);
                return (
                <tr key={part.id} style={dup ? { background: 'rgba(245, 158, 11, 0.05)' } : undefined}>
                  <td style={{ textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.78rem' }}>{idx + 1}</td>
                  <td><code style={{ fontSize: '0.78rem', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{part.code || '-'}</code></td>
                  <td style={{ fontWeight: 500 }}>
                    {part.name}
                    {dup && (
                      <span title="Nama item ini sama dengan item lain (duplikat)" style={{
                        marginLeft: 6, fontSize: '0.68rem', fontWeight: 600,
                        background: '#fef3c7', color: '#b45309', padding: '1px 7px', borderRadius: 10,
                        whiteSpace: 'nowrap',
                      }}>
                        ⚠ Duplikat
                      </span>
                    )}
                  </td>
                  <td>{part.unit || '-'}</td>
                  <td style={{ textAlign: 'right', fontSize: '0.82rem', color: '#64748b' }}>{formatPrice(part.harga_beli)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--gray-700)' }}>{formatPrice(part.harga_jual || part.price)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 28, height: 24, padding: '0 8px',
                      borderRadius: 12,
                      fontSize: '0.78rem', fontWeight: 600,
                      background: (part.stock || 0) > (part.min_stok || 0) ? '#e8f5e9' : '#fff3e0',
                      color: (part.stock || 0) > (part.min_stok || 0) ? '#2e7d32' : '#e65100',
                    }}>
                      {part.stock ?? 0}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.78rem', color: part.min_stok > 0 ? '#64748b' : '#94a3b8' }}>{part.min_stok || '-'}</td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {part.stok_per_gudang && Object.keys(part.stok_per_gudang).length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {Object.entries(part.stok_per_gudang).map(([gid, stok]) => {
                          const g = gudangList.find(x => String(x.id) === String(gid));
                          return (
                            <span key={gid} style={{ whiteSpace: 'nowrap' }}>
                              {g ? g.nama : 'Gudang #' + gid}: <strong>{stok}</strong>
                            </span>
                          );
                        })}
                      </div>
                    ) : dup ? (
                      // Item duplikat: tampilkan rincian stok semua baris senama
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {dupGroup.map(m => (
                          <span key={m.id} style={{
                            whiteSpace: 'nowrap',
                            fontWeight: m.id === part.id ? 600 : 400,
                            color: m.id === part.id ? 'var(--gray-700)' : 'var(--gray-500)',
                          }}>
                            {m.code || '#' + m.id}: <strong>{m.stock ?? 0}</strong>{m.gudang_nama ? ` · ${m.gudang_nama}` : ''}
                          </span>
                        ))}
                        <span style={{ color: 'var(--info)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          Total: {dupGroup.reduce((a, m) => a + (Number(m.stock) || 0), 0)}
                        </span>
                      </div>
                    ) : (part.gudang_nama || '-')}
                  </td>
                  <td>
                    {part.kategori ? (
                      <span className="badge" style={{ background: '#e6fafc', color: '#0e8c8c', fontSize: '0.7rem' }}>{part.kategori}</span>
                    ) : '-'}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>{part.rak || '-'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{part.warranty || '-'}</td>
                  {(hasMenuAction('inventori', 'edit') || hasMenuAction('inventori', 'delete')) && (
                    <td style={{ textAlign: 'center' }}>
                      <div className="action-btns" style={{ justifyContent: 'center' }}>
                        {hasMenuAction('inventori', 'edit') && (
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(part)}>
                            <Edit2 size={14} />
                          </button>
                        )}
                        {hasMenuAction('inventori', 'delete') && (
                          <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(part)}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={hasMenuAction('inventori', 'edit') || hasMenuAction('inventori', 'delete') ? 13 : 12} className="text-center text-muted">Tidak ada data sparepart</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Export Excel Modal — pilih semua data / hasil filter */}
      <ExportExcelModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Excel — Inventori Sparepart"
        allCount={totalAll || parts.length}
        filteredCount={filtered.length}
        filterSummary={exportFilterSummary}
        busy={exporting}
        onExportAll={exportAllData}
        onExportFiltered={() => { setShowExportModal(false); tulisExcelSparepart(filtered, 'Filter'); }}
      />

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPart ? 'Edit Sparepart' : 'Tambah Sparepart'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid form-grid-3">
                <div className="form-group"><label>Kode</label><input type="text" value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder="BRK-001" /></div>
                <div className="form-group"><label>Nama *</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nama sparepart" /></div>
                <div className="form-group"><label>Satuan</label><input type="text" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="PCS, LBR, KG" /></div>
                <div className="form-group"><label>Harga Beli</label><input type="text" inputMode="numeric" value={form.harga_beli ? Number(form.harga_beli).toLocaleString('id-ID') : ''} onChange={e => setForm({...form, harga_beli: e.target.value.replace(/\D/g, '')})} placeholder="0" /></div>
                <div className="form-group"><label>Harga Jual</label><input type="text" inputMode="numeric" value={form.harga_jual ? Number(form.harga_jual).toLocaleString('id-ID') : ''} onChange={e => setForm({...form, harga_jual: e.target.value.replace(/\D/g, '')})} placeholder="0" /></div>
                <div className="form-group"><label>Harga (lama)</label><input type="text" inputMode="numeric" value={form.price ? Number(form.price).toLocaleString('id-ID') : ''} onChange={e => setForm({...form, price: e.target.value.replace(/\D/g, '')})} placeholder="0" /></div>
                <div className="form-group">
                  <label>Stok Total</label>
                  <input type="number" value={sumPerGudang(form.stok_per_gudang) > 0 ? sumPerGudang(form.stok_per_gudang) : form.stock}
                    disabled={sumPerGudang(form.stok_per_gudang) > 0}
                    onChange={e => setForm({...form, stock: e.target.value})} placeholder="0" />
                  {sumPerGudang(form.stok_per_gudang) > 0 && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--info)', marginTop: 2 }}>Otomatis = total stok per gudang</div>
                  )}
                </div>
                <div className="form-group"><label>Min. Stok</label><input type="number" value={form.min_stok} onChange={e => setForm({...form, min_stok: e.target.value})} placeholder="0" /></div>
                <div className="form-group">
                  <label>Kategori</label>
                  {!showQuickAdd ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select value={form.kategori} onChange={e => setForm({...form, kategori: e.target.value})} style={{ flex: 1 }}>
                        <option value="">-- Pilih Kategori --</option>
                        {kategoriList.map((k, i) => (
                          <option key={i} value={k.kategori}>{k.kategori} ({k.jumlah_item} item, stok: {k.total_stok})</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => setShowQuickAdd(true)} title="Tambah kategori baru"
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px dashed var(--gray-300)', background: 'transparent', color: 'var(--info)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="text" value={quickAddName} onChange={e => setQuickAddName(e.target.value)}
                        placeholder="Nama kategori baru..." autoFocus
                        style={{ flex: 1, padding: '8px 12px', border: '2px solid var(--info)', borderRadius: 6, fontSize: '0.85rem', outline: 'none' }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && quickAddName.trim() && !quickAddSaving) {
                            e.preventDefault();
                            setQuickAddSaving(true);
                            try {
                              await createKategori(quickAddName.trim());
                              await loadKategori();
                              setForm(f => ({ ...f, kategori: quickAddName.trim() }));
                              setShowQuickAdd(false);
                              setQuickAddName('');
                            } catch (err) {
                              const msg = err.response?.data?.error || 'Gagal';
                              if (msg.includes('sudah ada')) alert('Kategori sudah ada, silakan pilih dari dropdown.');
                              else alert(msg);
                            }
                            setQuickAddSaving(false);
                          } else if (e.key === 'Escape') {
                            setShowQuickAdd(false);
                            setQuickAddName('');
                          }
                        }}
                      />
                      <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
                        {quickAddSaving ? 'Menyimpan...' : 'Enter simpan · Esc batal'}
                      </span>
                    </div>
                  )}
                  <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Tags size={10} />
                    Kelola kategori di menu <strong>Kategori Sparepart</strong>
                  </div>
                </div>
                <div className="form-group"><label>Rak</label><input type="text" value={form.rak} onChange={e => setForm({...form, rak: e.target.value})} placeholder="A-01" /></div>
                <div className="form-group"><label>Garansi</label><input type="text" value={form.warranty} onChange={e => setForm({...form, warranty: e.target.value})} placeholder="1 bulan" /></div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Stok per Gudang <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>(opsional — untuk item yang tersimpan di beberapa gudang)</span></label>
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8,
                    border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10, background: 'var(--gray-50)',
                  }}>
                    {gudangList.length === 0 && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Belum ada gudang — buat gudang dulu di menu Gudang.</span>
                    )}
                    {gudangList.map(g => (
                      <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ flex: 1, fontSize: '0.78rem', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {g.nama}
                        </label>
                        <input type="number" min="0" placeholder="0"
                          value={form.stok_per_gudang[String(g.id)] ?? ''}
                          onChange={e => setForm({ ...form, stok_per_gudang: { ...form.stok_per_gudang, [String(g.id)]: e.target.value } })}
                          style={{ width: 90, textAlign: 'right' }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 3 }}>
                    Stok total otomatis = jumlah semua gudang. Biarkan 0 semua untuk stok global (tanpa gudang).
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
